// client/src/components/admin/AdminInviteCodes.js
import React, { useState } from 'react'
import useInviteCodes from '../../hooks/useInviteCodes'
import PageLayout from '../common/layout/PageLayout'
import './AdminInviteCodes.css'

function AdminInviteCodes() {
  // Form state (UI-specific)
  const [newCode, setNewCode] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [maxUses, setMaxUses] = useState(1)
  const [restrictedToEmail, setRestrictedToEmail] = useState('')
  const [expandedCodeId, setExpandedCodeId] = useState(null)

  // Data and operations from custom hook
  const {
    codes,
    loading,
    error,
    creators,
    createCode,
    disableCode,
    deleteCode,
  } = useInviteCodes()

  const handleCreate = async (e) => {
    e.preventDefault()

    // Validate email format if provided
    if (restrictedToEmail && restrictedToEmail.trim()) {
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
      if (!emailRegex.test(restrictedToEmail.trim())) {
        alert('Please enter a valid email address')
        return
      }
    }

    try {
      await createCode({
        code: newCode || undefined,
        expires_at: expiresAt || undefined,
        max_uses: maxUses,
        restricted_to_email: restrictedToEmail || undefined,
      })
      // Reset form on successful creation
      setNewCode('')
      setExpiresAt('')
      setMaxUses(1)
      setRestrictedToEmail('')
    } catch {
      // Error handling is managed by the hook
    }
  }

  const handleDisable = async (id) => {
    try {
      await disableCode(id)
    } catch {
      // Error handling is managed by the hook
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invite code?')) return
    try {
      await deleteCode(id)
    } catch {
      // Error handling is managed by the hook
    }
  }

  const toggleExpanded = (codeId) => {
    setExpandedCodeId(expandedCodeId === codeId ? null : codeId)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString()
  }

  // Total column count for the table (used for colspan in expanded details)
  const TOTAL_COLUMNS = 9

  if (loading)
    return (
      <PageLayout title="Invite Codes" noCard={true}>
        <p>Loading...</p>
      </PageLayout>
    )

  if (error)
    return (
      <PageLayout title="Invite Codes" noCard={true}>
        <p className="text-danger">{error}</p>
      </PageLayout>
    )

  return (
    <PageLayout title="Invite Codes" noCard={true}>
      {/* Informational Section */}
      <div className="invite-codes-info-section">
        <h5>About Invite Codes</h5>
        <p>
          Invite codes control access to ServeSync Tennis. Create codes to
          invite new users or grant access to league registrations.
        </p>
        <p className="mb-0">
          <strong>Usage Types:</strong> Codes can be used for{' '}
          <strong>Account Signups</strong> (creating new user accounts) or{' '}
          <strong>League Registrations</strong> (joining specific leagues).
        </p>
      </div>

      {/* Create Form Section */}
      <div className="invite-codes-form-section">
        <h5>Create New Invite Code</h5>
        <form onSubmit={handleCreate}>
          <div className="row g-3">
            <div className="col-12 col-md-6 col-lg-3">
              <label htmlFor="customCode" className="form-label">
                Custom Code
              </label>
              <input
                id="customCode"
                type="text"
                className="form-control"
                placeholder="e.g., WELCOME2024"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                aria-describedby="customCodeHelp"
              />
              <small id="customCodeHelp" className="form-helper-text">
                Leave blank to auto-generate a random code
              </small>
            </div>
            <div className="col-12 col-md-6 col-lg-2">
              <label htmlFor="maxUses" className="form-label">
                Max Uses
              </label>
              <input
                id="maxUses"
                type="number"
                min="1"
                className="form-control"
                value={maxUses}
                onChange={(e) => setMaxUses(parseInt(e.target.value))}
                aria-describedby="maxUsesHelp"
              />
              <small id="maxUsesHelp" className="form-helper-text">
                How many times this code can be used
              </small>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <label htmlFor="expiresAt" className="form-label">
                Expiration Date
              </label>
              <input
                id="expiresAt"
                type="date"
                className="form-control"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                aria-describedby="expiresAtHelp"
              />
              <small id="expiresAtHelp" className="form-helper-text">
                Optional: Code expires at end of this date
              </small>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <label htmlFor="restrictedEmail" className="form-label">
                Restricted Email
              </label>
              <input
                id="restrictedEmail"
                type="email"
                className="form-control"
                placeholder="user@example.com"
                value={restrictedToEmail}
                onChange={(e) => setRestrictedToEmail(e.target.value)}
                aria-describedby="restrictedEmailHelp"
              />
              <small id="restrictedEmailHelp" className="form-helper-text">
                Optional: Limit code to specific email address
              </small>
            </div>
            <div className="col-12 col-lg-1 d-flex align-items-end">
              <button className="btn btn-primary w-100" type="submit">
                Create
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Invite Codes Table */}
      {codes.length === 0 ? (
        <div className="invite-codes-empty-state">
          <div
            className="invite-codes-empty-state-icon"
            role="img"
            aria-label="Invite code icon"
          >
            🎟️
          </div>
          <h5>No Invite Codes Yet</h5>
          <p>
            You haven't created any invite codes. Use the form above to create
            your first invite code and start inviting users to ServeSync Tennis.
          </p>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Code</th>
                <th>Created By</th>
                <th>Created At</th>
                <th>Restricted Email</th>
                <th>Expires</th>
                <th>Uses</th>
                <th>Disabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <React.Fragment key={c.id}>
                  <tr>
                    <td>
                      <button
                        onClick={() => toggleExpanded(c.id)}
                        className="btn btn-sm btn-link p-0 text-decoration-none"
                        aria-label={
                          expandedCodeId === c.id
                            ? 'Collapse details'
                            : 'Expand details'
                        }
                        aria-expanded={expandedCodeId === c.id}
                      >
                        {expandedCodeId === c.id ? '🔽' : '▶️'}
                      </button>
                    </td>
                    <td>
                      <strong>{c.code}</strong>
                    </td>
                    <td>
                      {c.created_by_user_id
                        ? creators[c.created_by_user_id] ||
                          `User #${c.created_by_user_id}`
                        : '—'}
                    </td>
                    <td>
                      {c.created_at ? formatDate(c.created_at) : 'Unknown'}
                    </td>
                    <td>
                      {c.restricted_to_email ? (
                        <span className="badge bg-info text-dark">
                          {c.restricted_to_email}
                        </span>
                      ) : (
                        <span className="text-muted">Any</span>
                      )}
                    </td>
                    <td>
                      {c.expires_at
                        ? new Date(c.expires_at).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td>
                      {c.used_by?.length || 0}/{c.max_uses}
                    </td>
                    <td>{c.disabled ? 'Yes' : 'No'}</td>
                    <td>
                      {!c.disabled && (
                        <button
                          className="btn btn-sm btn-warning me-2"
                          onClick={() => handleDisable(c.id)}
                        >
                          Disable
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(c.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  {expandedCodeId === c.id && (
                    <tr>
                      <td
                        colSpan={TOTAL_COLUMNS}
                        style={{ backgroundColor: '#f8f9fa', padding: '1rem' }}
                      >
                        <div className="row">
                          <div className="col-12 col-md-6">
                            <h6>
                              Account Signups (
                              {c.usage_details?.signup?.length || 0})
                            </h6>
                            {c.usage_details?.signup &&
                            c.usage_details.signup.length > 0 ? (
                              <ul className="list-unstyled">
                                {c.usage_details.signup.map((usage) => (
                                  <li key={usage.user_id} className="mb-2">
                                    <strong>{usage.display_name}</strong>
                                    {usage.unknown && (
                                      <span className="badge bg-secondary ms-2">
                                        Unknown Type
                                      </span>
                                    )}
                                    <br />
                                    <small className="text-muted">
                                      Email: {usage.email || 'N/A'} | Signed up:{' '}
                                      {formatDate(usage.used_at)}
                                    </small>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-muted">
                                No signups with this code
                              </p>
                            )}
                          </div>
                          <div className="col-12 col-md-6">
                            <h6>
                              League Registrations (
                              {c.usage_details?.league_registration?.length ||
                                0}
                              )
                            </h6>
                            {c.usage_details?.league_registration &&
                            c.usage_details.league_registration.length > 0 ? (
                              <ul className="list-unstyled">
                                {c.usage_details.league_registration.map(
                                  (usage) => (
                                    <li key={usage.user_id} className="mb-2">
                                      <strong>{usage.display_name}</strong>
                                      <br />
                                      <small className="text-muted">
                                        Email: {usage.email || 'N/A'} |
                                        Registered: {formatDate(usage.used_at)}
                                      </small>
                                    </li>
                                  ),
                                )}
                              </ul>
                            ) : (
                              <p className="text-muted">
                                No league registrations with this code
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageLayout>
  )
}

export default AdminInviteCodes
